// 创建微博预约
import React, { useContext, useEffect, useState } from "react"
import moment from "moment"
import { CalendarContext } from "Components/Calendar/context"
import { Board, BoardModifyDialog } from "Types/board"
import { useInputChange } from "Hooks/basic"
import { useAddBoard, useUpdateBoard } from "Hooks/board"
import { useCreateModalTimeStr } from "Components/Calendar/CalendarModal/hooks"
import { timeFormatStr } from "Methods/basic"
import request from "Methods/request"
import { InitialModal } from "Methods/modal"
import { AppContext } from "Views/Home/context"
import { checkUserPrivilege } from "Methods/privilege"

// 微博预约，额外项
export function useWeiboExternals(board?: Board | null) {
    const initialExternal = {
        hours: "00",
        minutes: "00",
        contentType: "",
        isLottery: false,
        isMicro: false,
        articleTypeId: 0,
    }
    const [external, setExternals] = useState(initialExternal)
    const resetExternal = () => {
        setExternals(initialExternal)
    }
    useEffect(() => {
        if (board) {
            setExternals({
                ...initialExternal,
                articleTypeId: board.article_type === null || board.article_type.hidden ? 0 : board.article_type_id,
                isLottery: board.is_lottery,
                isMicro: board.is_micro,
                contentType: board.content_type,
            })
        }
        // eslint-disable-next-line
    }, [board])
    const handleChangeArticleType = (value: number) => {
        setExternals({
            ...external,
            articleTypeId: Number(value),
        })
    }
    const handleChangeDate = (data: any) => {
        setExternals({
            ...external,
            ...data,
        })
    }
    const handleChangeContentType = (value: string) => {
        setExternals({
            ...external,
            contentType: value,
        })
    }
    const handleChangeLottery = (event: React.ChangeEvent<HTMLInputElement>) => {
        setExternals({
            ...external,
            isLottery: event.target.checked,
        })
    }
    const handleChangeMicro = (event: React.ChangeEvent<HTMLInputElement>) => {
        setExternals({
            ...external,
            isMicro: event.target.checked,
        })
    }
    return {
        external, handleChangeDate,
        handleChangeContentType,
        handleChangeLottery,
        handleChangeMicro,
        resetExternal,
        handleChangeArticleType,
    }
}

export function useCreateWeibo() {
    const {
        external, handleChangeMicro,
        handleChangeLottery,
        handleChangeContentType,
        handleChangeDate,
        resetExternal,
        handleChangeArticleType,
    } = useWeiboExternals()
    const { boardModifyDialog, handleChangeBoardModifyDialog } = useContext(CalendarContext)
    const { roleMap } = useContext(AppContext)
    const { isBusiness } = roleMap
    const dialogData = boardModifyDialog as BoardModifyDialog
    const { text, handleChange, clearText } = useInputChange("")
    const { text: projectId, handleChange: handleProjectIdChange, clearText: clearProjectId, setText: setProjectId } = useInputChange("")
    const { text: projectMoney, handleChange: handleProjectMoneyChange, clearText: clearProjectMoney, setText: setProjectMoney } = useInputChange("")
    const { add } = useAddBoard()
    const disabled = text === "" || external.contentType === "" || (isBusiness && (projectId.length > 50 || projectMoney.length > 50 || projectId === ""))
    const callback = async () => {
        let time = 0
        if (dialogData.place.id === 95) {
            time = Number(external.hours) * 3600 * 1000 + Number(external.minutes) * 60 * 1000
        }
        await add({
            name: text,
            ad_place_id: dialogData.place.id,
            ad_times: [(dialogData.date.getTime() + time)],
            content_type: external.contentType,
            is_lottery: external.isLottery,
            is_micro: external.isMicro,
            article_type_id: external.articleTypeId,
            project_id: projectId,
            project_money: projectMoney,
        })
        resetExternal()
    }
    const cancelCallback = () => {
        handleChangeBoardModifyDialog(null)
        clearText()
        clearProjectId()
        clearProjectMoney()
        resetExternal()
    }
    const dateStr = moment(dialogData.date).format(timeFormatStr.all)
    return {
        callback, cancelCallback,
        dialogData, disabled, dateStr,
        text, handleChange,
        projectId, handleProjectIdChange,
        projectMoney, handleProjectMoneyChange,
        isBusiness,
        external, handleChangeDate,
        handleChangeContentType,
        handleChangeLottery,
        handleChangeMicro,
        handleChangeArticleType,
    }
}
// 创建多个微博预约
export function useCreateMultipleWeibo() {
    const {
        external, handleChangeMicro,
        handleChangeLottery,
        handleChangeContentType,
        handleChangeDate,
        resetExternal,
        handleChangeArticleType,
    } = useWeiboExternals()
    const {
        boardModifyDialog, handleChangeBoardModifyDialog,
        handleChangeSelectPlaceId, selectPlaceId,
        selectDateRange,
    } = useContext(CalendarContext)
    const dialogData = boardModifyDialog as BoardModifyDialog
    const { modalTimeStr, adTimes } = useCreateModalTimeStr(selectDateRange)
    const { text, handleChange, clearText } = useInputChange("")
    const { add } = useAddBoard()
    const callback = async () => {
        let time = 0
        if (selectPlaceId === 95) {
            time = Number(external.hours) * 3600 * 1000 + Number(external.minutes) * 60 * 1000
        }
        await add({
            name: text,
            ad_place_id: selectPlaceId,
            ad_times: adTimes.map((d: number) => (d + time)),
            content_type: external.contentType,
            is_lottery: external.isLottery,
            is_micro: external.isMicro,
            article_type_id: external.articleTypeId,
        })
        resetExternal()
    }
    const cancelCallback = () => {
        handleChangeBoardModifyDialog(null)
        clearText()
        handleChangeSelectPlaceId(0)
        resetExternal()
    }
    const disabled = text === ""
    return {
        callback, cancelCallback,
        dialogData, disabled, modalTimeStr,
        text, handleChange,
        external, handleChangeDate,
        handleChangeContentType,
        handleChangeLottery,
        handleChangeMicro,
        selectPlaceId,
        handleChangeArticleType,
    }
}
// 修改微博预约
export function useUpdateWeibo() {
    const { boardModifyDialog, handleChangeBoardModifyDialog, handleSetEditMaterial } = useContext(CalendarContext)
    const { update } = useUpdateBoard()
    const { handleChangeModal, privilegeMap, roleMap, user } = useContext(AppContext)
    const { update: materialUpdate = false } = checkUserPrivilege(privilegeMap, "material")
    const dialogData = boardModifyDialog as BoardModifyDialog
    const { isPass, isThreeDay } = dialogData
    const board = dialogData.board as Board
    const { text, handleChange, clearText } = useInputChange(board.name)
    const { text: projectId, handleChange: handleProjectIdChange, clearText: clearProjectId, setText: setProjectId } = useInputChange(board.project_id)
    const { text: projectMoney, handleChange: handleProjectMoneyChange, clearText: clearProjectMoney, setText: setProjectMoney } = useInputChange(board.project_money)
    const { isUser, isBusiness } = roleMap
    const isMine = dialogData.board && dialogData.board.creator_id === user.uid

    const {
        external,
        resetExternal,
        handleChangeArticleType,
        handleChangeMicro,
        handleChangeLottery,
        handleChangeContentType,
    } = useWeiboExternals(board)
    const callback = async () => {
        await update({
            variables: {
                id: board.id,
                name: text,
                article_type_id: external.articleTypeId,
                content_type: external.contentType,
                is_lottery: external.isLottery,
                is_micro: external.isMicro,
                project_id: projectId || '',
                project_money: projectMoney || '',
            },
        })
        resetExternal()
    }
    const cancelCallback = () => {
        handleChangeBoardModifyDialog(null)
        clearText()
        clearProjectId()
        clearProjectMoney()
        resetExternal()
    }
    const editCallback = async () => {
        const { data } = await request.get(`/apis/material/${board.material.id}`)
        handleSetEditMaterial({
            open: true,
            update: materialUpdate,
            isBom: isUser,
            modifyData: data,
            handleClose: () => {
                handleSetEditMaterial({
                    modifyData: null,
                    open: false,
                    update: false,
                    isBom: false,
                    handleClose: () => { },
                    handleConfirm: () => { },
                })
            },
            handleConfirm: async (newItem) => {
                try {
                    await request.put(`/apis/material/${newItem.id}/update`, newItem)
                    handleChangeModal({
                        show: true,
                        type: "success",
                        title: "修改成功！",
                        timeout: 800,
                        onConfirm: () => {
                            handleChangeModal(InitialModal)
                        },
                    })
                } catch (err) {
                    if (!navigator.onLine) {
                        handleChangeModal({
                            show: true,
                            type: "warning",
                            customButtons: [],
                            title: "oops! 断网啦！",
                            timeout: 3000,
                            onConfirm: () => {
                                handleChangeModal(InitialModal)
                            },
                        })
                        return
                    }
                    const { response: { data = {} } = {}, message } = err
                    handleChangeModal({
                        show: true,
                        type: "warning",
                        customButtons: [],
                        title: data.message || message,
                        timeout: 3000,
                        onConfirm: () => {
                            handleChangeModal(InitialModal)
                        },
                    })
                }
                handleSetEditMaterial({
                    modifyData: null,
                    open: false,
                    update: false,
                    isBom: false,
                    handleClose: () => { },
                    handleConfirm: () => { },
                })
            },
        })
    }
    const dateStr = moment(board.ad_time).format(timeFormatStr.whole)
    const disabled = text === "" || !Boolean(external.articleTypeId) || (isBusiness && (projectId.length > 50 || projectMoney.length > 50 || projectId === ""))
    return {
        callback, cancelCallback, disabled,
        dialogData, dateStr, board, text,
        external, isBusiness, isMine, isPass, isThreeDay,
        projectId, handleProjectIdChange,
        projectMoney, handleProjectMoneyChange,
        handleChangeArticleType,
        handleChange,
        handleChangeMicro,
        handleChangeLottery,
        handleChangeContentType,
        editCallback,
    }
}
// 创建微博草稿
export function useCreateWeiboDraft() {
    const {
        external, handleChangeMicro,
        handleChangeLottery,
        handleChangeContentType,
        handleChangeDate,
        resetExternal,
        handleChangeArticleType,
    } = useWeiboExternals()
    const { boardModifyDialog, handleChangeBoardModifyDialog } = useContext(CalendarContext)
    const dialogData = boardModifyDialog as BoardModifyDialog
    const { text, handleChange, clearText } = useInputChange("")
    const { add } = useAddBoard()
    const disabled = text === "" || external.contentType === ""
    const callback = async () => {
        await add({
            name: text,
            ad_times: [new Date().getTime()],
            content_type: external.contentType,
            is_lottery: external.isLottery,
            is_micro: external.isMicro,
            is_draft: true,
            article_type_id: external.articleTypeId,
        })
        resetExternal()
    }
    const cancelCallback = () => {
        handleChangeBoardModifyDialog(null)
        clearText()
        resetExternal()
    }
    return {
        callback, cancelCallback,
        dialogData, disabled,
        text, handleChange,
        external, handleChangeDate,
        handleChangeContentType,
        handleChangeLottery,
        handleChangeMicro,
        handleChangeArticleType,
    }
}
